import './immobile-image-comparison.scss';

const COMPONENT_SELECTOR = '[data-image-comparison]';
const HANDLE_SELECTOR = '[data-image-comparison-handle]';
const DIVIDER_SELECTOR = '[data-image-comparison-divider]';
const TOGGLE_SELECTOR = '[data-image-comparison-toggle]';

const CSS_VAR = '--ic-position';
const DEFAULT_POSITION = 50;
const KEYBOARD_STEP = 2;

const BEM = {
  LABEL_ACTIVE: 'immobile-image-comparison__toggle-label--active',
};

/**
 * version    1.0
 * class      ImmobileImageComparison
 * classdesc  Before/after image comparison component.
 *            Desktop (>= md): draggable divider controlled by mouse/touch/keyboard.
 *            Mobile (< md): toggle switch to switch between the two images.
 */
export class ImmobileImageComparison {
  constructor() {
    this._figure = document.querySelector(COMPONENT_SELECTOR);
    if (!this._figure) return;

    this._handle = this._figure.querySelector(HANDLE_SELECTOR);
    this._divider = this._figure.querySelector(DIVIDER_SELECTOR);
    this._sideReal = this._figure.querySelector('.immobile-image-comparison__side--real');
    this._sideRendering = this._figure.querySelector('.immobile-image-comparison__side--rendering');
    this._toggle = document.querySelector(TOGGLE_SELECTOR);
    this._labelReal = document.querySelector('#ic-label-real');
    this._labelRendering = document.querySelector('#ic-label-rendering');

    this._state = {
      isDragging: false,
      position: DEFAULT_POSITION,
    };

    this._boundOnMouseMove = this._onMouseMove.bind(this);
    this._boundOnMouseUp = this._onMouseUp.bind(this);
    this._boundOnTouchMove = this._onTouchMove.bind(this);
    this._boundOnTouchEnd = this._onTouchEnd.bind(this);

    this._initDesktopSlider();
    this._initMobileToggle();
  }

  // Desktop slider
  _initDesktopSlider() {
    if (!this._handle) return;

    this._setPosition(DEFAULT_POSITION);

    [this._handle, this._divider].forEach((el) => {
      if (!el) return;
      el.addEventListener('mousedown', (e) => this._onHandleMouseDown(e));
      el.addEventListener('touchstart', (e) => this._onHandleTouchStart(e), { passive: false });
    });

    this._handle.addEventListener('keydown', (e) => this._onHandleKeyDown(e));
  }

  _onHandleMouseDown(e) {
    e.preventDefault();
    this._state.isDragging = true;
    document.addEventListener('mousemove', this._boundOnMouseMove);
    document.addEventListener('mouseup', this._boundOnMouseUp);
  }

  _onMouseMove(e) {
    if (!this._state.isDragging) return;
    this._updatePositionFromClientX(e.clientX);
  }

  _onMouseUp() {
    this._state.isDragging = false;
    document.removeEventListener('mousemove', this._boundOnMouseMove);
    document.removeEventListener('mouseup', this._boundOnMouseUp);
  }

  _onHandleTouchStart(e) {
    e.preventDefault();
    this._state.isDragging = true;
    document.addEventListener('touchmove', this._boundOnTouchMove, { passive: false });
    document.addEventListener('touchend', this._boundOnTouchEnd);
  }

  _onTouchMove(e) {
    if (!this._state.isDragging) return;
    e.preventDefault();
    this._updatePositionFromClientX(e.touches[0].clientX);
  }

  _onTouchEnd() {
    this._state.isDragging = false;
    document.removeEventListener('touchmove', this._boundOnTouchMove);
    document.removeEventListener('touchend', this._boundOnTouchEnd);
  }

  _onHandleKeyDown(e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this._setPosition(Math.max(0, this._state.position - KEYBOARD_STEP));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this._setPosition(Math.min(100, this._state.position + KEYBOARD_STEP));
    }
  }

  _updatePositionFromClientX(clientX) {
    const rect = this._figure.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.min(Math.max((x / rect.width) * 100, 0), 100);
    this._setPosition(pct);
  }

  _setPosition(pct) {
    this._state.position = pct;
    this._figure.style.setProperty(CSS_VAR, `${pct}%`);
  }

  // Mobile toggle
  _initMobileToggle() {
    if (!this._toggle) return;

    this._toggle.addEventListener('change', () => this._onToggleChange());
  }

  _onToggleChange() {
    const showRendering = this._toggle.checked;

    if (this._sideReal) {
      this._sideReal.classList.toggle('d-none', showRendering);
    }
    if (this._sideRendering) {
      this._sideRendering.classList.toggle('d-none', !showRendering);
    }

    if (this._labelReal) {
      this._labelReal.classList.toggle(BEM.LABEL_ACTIVE, !showRendering);
    }
    if (this._labelRendering) {
      this._labelRendering.classList.toggle(BEM.LABEL_ACTIVE, showRendering);
    }
  }
}
